import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { classifyServiceKind, createInventoryItemSchema, type AdjustInventoryInput, type InventoryItem } from '@wrapforce/shared';
import {
  INVENTORY_CATEGORIES, INVENTORY_CATEGORY_UNIT, INVENTORY_UNITS, type InventoryCategory,
} from '@wrapforce/shared/constants';

type InventoryItemFormValues = z.input<typeof createInventoryItemSchema>;
import { Plus, Search, Settings2, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useCatalog } from '@/features/quotes/api';
import { useUploadMedia } from '@/features/media/api';
import { useCreateMaterial, useUpdateMaterial } from '@/features/settings/api';
import {
  useAdjustInventory, useBrands, useCreateBrand, useCreateInventoryItem, useDeleteBrand, useInventory, useInventoryMovements,
  useUpdateBrand, useUpdateInventoryItem,
} from './api';
import { useDebounce } from '@/lib/useDebounce';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Sheet } from '@/components/ui/sheet';
import { Combobox } from '@/components/ui/combobox';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';

const STATUS_STYLE: Record<string, string> = { ok: 'bg-success/15 text-success', low: 'bg-warning/15 text-warning', out: 'bg-danger/15 text-danger' };

// Per-unit price label depends on the item's unit ('bottle'/'sqft' get a
// friendlier phrasing, everything else falls back to a generic label) - see
// form.labels.pricePerUnit.* in inventory.json.
function pricePerUnitLabelKey(unit: string): string {
  if (unit === 'bottle') return 'form.labels.pricePerUnitBottle';
  if (unit === 'sqft') return 'form.labels.pricePerUnitSqft';
  if (unit === 'ml') return 'form.labels.pricePerUnitMl';
  return 'form.labels.pricePerUnitGeneric';
}

function centsToDollarsInput(cents: number | undefined): string {
  return cents !== undefined ? (cents / 100).toFixed(2) : '';
}

// classifyServiceKind groups catalog service types by name into
// wrap/ppf/tint/ceramic, each with its own materials (used only by Wrap's
// BrandField below, see the comment there). A material's isActive flag
// means "usable for quote pricing" (PPF/Tint/Ceramic materials were
// deactivated for that in 0029_legacy_pricing_deactivate.sql, since those now
// price off fixed tier tables), so it's ignored here.
function useServiceTypeForCategory(workspaceId: string | null, category: InventoryCategory) {
  const { data: catalog } = useCatalog(workspaceId);
  return catalog?.serviceTypes.find((s) => classifyServiceKind(s.name) === category);
}

function useAllMaterials(workspaceId: string | null) {
  const { data: catalog } = useCatalog(workspaceId);
  return catalog?.serviceTypes.flatMap((s) => s.materials) ?? [];
}

// Wrap items link to a real catalog material (materialId) because that FK
// also drives New Quote's stock-availability lookup, so its Brand field is
// this combobox, backed by createMaterial/updateMaterial. Every other
// category has no such integration to preserve, so they use BrandComboField
// below instead, backed by the standalone brands table (0032_brands.sql).
// Creating a wrap material needs more than a name (series, cost, price
// multiplier), so "Add a New Brand" opens a small form rather than creating
// instantly like BrandComboField does.
function BrandField({
  workspaceId, category, value, onChange,
}: {
  workspaceId: string | null;
  category: InventoryCategory;
  value: string;
  onChange: (materialId: string) => void;
}) {
  const { t } = useTranslation('inventory');
  const serviceType = useServiceTypeForCategory(workspaceId, category);
  const createMaterial = useCreateMaterial(workspaceId);
  const updateMaterial = useUpdateMaterial(workspaceId);
  const [mode, setMode] = useState<'idle' | 'add' | 'edit'>('idle');
  const [brandName, setBrandName] = useState('');
  const [seriesName, setSeriesName] = useState('');

  const materials = serviceType?.materials ?? [];
  const selected = materials.find((m) => m.id === value);
  const selectedBrand = selected?.brand ?? '';
  const brandOptions = Array.from(new Set(materials.map((m) => m.brand)))
    .sort()
    .map((b) => ({ value: b, label: b }));
  const seriesOptions = materials
    .filter((m) => m.brand === selectedBrand)
    .map((m) => ({ value: m.id, label: m.series || t('form.noSeries') }));

  function handleBrandChange(brand: string) {
    const firstMatch = materials.find((m) => m.brand === brand);
    onChange(firstMatch?.id ?? '');
  }

  function openAdd() {
    setBrandName('');
    setSeriesName('');
    setMode('add');
  }

  function openEdit() {
    if (!selected) return;
    setBrandName(selected.brand);
    setSeriesName(selected.series);
    setMode('edit');
  }

  async function handleAdd() {
    if (!serviceType || !brandName.trim()) return;
    const { id } = await createMaterial.mutateAsync({
      serviceTypeId: serviceType.id,
      input: { brand: brandName.trim(), series: seriesName.trim() || undefined, costPerSqftCents: 0, priceMultiplier: 1 },
    });
    onChange(id);
    setMode('idle');
    toast.success(t('toast.brandAdded'));
  }

  async function handleEditSave() {
    if (!selected || !brandName.trim()) return;
    await updateMaterial.mutateAsync({ id: selected.id, input: { brand: brandName.trim(), series: seriesName.trim() } });
    setMode('idle');
    toast.success(t('toast.brandUpdated'));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="materialId">{t('form.labels.brand')}</Label>
        {mode !== 'idle' ? (
          <button type="button" onClick={() => setMode('idle')} className="text-[11px] font-semibold text-accent-ink hover:underline">
            {t('common:actions.cancel')}
          </button>
        ) : serviceType ? (
          <div className="flex gap-2">
            {selected ? (
              <button type="button" onClick={openEdit} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('common:actions.edit')}
              </button>
            ) : null}
            <button type="button" onClick={openAdd} className="text-[11px] font-semibold text-accent-ink hover:underline">
              {t('actions.addNewBrand')}
            </button>
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Combobox
          id="materialId"
          value={selectedBrand}
          onChange={handleBrandChange}
          options={brandOptions}
          placeholder={t('form.selectBrand')}
          emptyText={t('empty.noBrands')}
        />
        <Combobox
          value={value}
          onChange={onChange}
          options={seriesOptions}
          placeholder={t('form.selectSeries')}
          emptyText={t('empty.noSeries')}
        />
      </div>
      {mode !== 'idle' && serviceType ? (
        <div className="flex gap-2">
          <Input placeholder={t('form.placeholders.brand')} value={brandName} onChange={(e) => setBrandName(e.target.value)} className="flex-1" />
          <Input placeholder={t('form.placeholders.seriesOptional')} value={seriesName} onChange={(e) => setSeriesName(e.target.value)} className="flex-1" />
          <Button
            type="button"
            size="sm"
            disabled={!brandName.trim()}
            loading={mode === 'add' ? createMaterial.isPending : updateMaterial.isPending}
            onClick={mode === 'add' ? handleAdd : handleEditSave}
          >
            {mode === 'add' ? t('actions.add') : t('common:actions.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// No catalog service_type backs 'other' (classifyServiceKind only covers
// wrap/ppf/tint/ceramic), and PPF/Tint/Ceramic materials were deactivated for
// quote pricing in 0029_legacy_pricing_deactivate.sql, so instead of the
// materials system, these categories tag brand via the standalone `brands`
// table (0032_brands.sql): pick an existing one from the dropdown, or use
// "Add a New Brand" to create one. Renaming/removing brands happens in
// ManageBrandsDialog.
function BrandComboField({ workspaceId, value, onChange }: { workspaceId: string | null; value: string; onChange: (brandId: string) => void }) {
  const { t } = useTranslation('inventory');
  const { data } = useBrands(workspaceId);
  const createBrand = useCreateBrand(workspaceId);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const options = (data?.items ?? []).map((b) => ({ value: b.id, label: b.name }));

  async function handleCreate() {
    if (!newName.trim()) return;
    const { id } = await createBrand.mutateAsync({ name: newName.trim() });
    onChange(id);
    setNewName('');
    setAdding(false);
    toast.success(t('toast.brandAdded'));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="brandId">{t('form.labels.brand')}</Label>
        <button type="button" onClick={() => setAdding((v) => !v)} className="text-[11px] font-semibold text-accent-ink hover:underline">
          {adding ? t('common:actions.cancel') : t('actions.addNewBrand')}
        </button>
      </div>
      <Combobox id="brandId" value={value} onChange={onChange} options={options} placeholder={t('form.selectBrand')} emptyText={t('empty.noBrands')} />
      {adding ? (
        <div className="flex gap-2">
          <Input
            placeholder={t('form.placeholders.brandName')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="flex-1"
          />
          <Button type="button" size="sm" disabled={!newName.trim()} loading={createBrand.isPending} onClick={handleCreate}>
            {t('actions.add')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type SwatchValue = { id: string; thumbUrl: string | null } | null;

// Optional photo of the actual film/material so AI mockups can match its
// exact color and finish. Uploads go through the shared media pipeline
// (kind: 'material_swatch') and only the returned media id is persisted on
// the inventory item; url/thumbUrl are re-signed server-side on read.
function SwatchUploadField({
  workspaceId, value, onChange,
}: {
  workspaceId: string | null;
  value: SwatchValue;
  onChange: (value: SwatchValue) => void;
}) {
  const { t } = useTranslation('inventory');
  const upload = useUploadMedia(workspaceId);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="swatchUpload">{t('form.labels.swatch')}</Label>
      <p className="text-xs text-muted">{t('form.swatchHelp')}</p>
      <div className="flex items-center gap-3">
        {value?.thumbUrl ? (
          <img src={value.thumbUrl} alt="" className="h-16 w-16 rounded-[var(--radius-input)] border border-border object-cover" />
        ) : null}
        <div className="flex flex-col items-start gap-1">
          <input
            id="swatchUpload"
            type="file"
            accept="image/*"
            disabled={upload.isPending}
            className="text-xs"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                const asset = await upload.mutateAsync({ file, kind: 'material_swatch' });
                onChange({ id: asset.id, thumbUrl: asset.thumbUrl });
              } catch {
                toast.error(t('toast.swatchUploadFailed'));
              }
            }}
          />
          {upload.isPending ? <span className="text-xs text-muted">{t('form.uploading')}</span> : null}
          {value ? (
            <button type="button" onClick={() => onChange(null)} className="text-[11px] font-semibold text-danger hover:underline">
              {t('actions.remove')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function InventoryPage() {
  const { t } = useTranslation('inventory');
  const { formatMoney } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [category, setCategory] = useState<InventoryCategory | undefined>(undefined);
  const [brand, setBrand] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [addOpen, setAddOpen] = useState(false);
  const [manageBrandsOpen, setManageBrandsOpen] = useState(false);
  const [selected, setSelected] = useState<InventoryItem | null>(null);
  const { data } = useInventory(workspaceId, { query: debouncedSearch.length >= 2 ? debouncedSearch : undefined, status, category, brand });
  const items = data?.items ?? [];
  const allMaterials = useAllMaterials(workspaceId);
  const { data: brandsData } = useBrands(workspaceId);
  const managedBrands = brandsData?.items ?? [];
  const brandNameById = new Map<string, string>(managedBrands.map((b): [string, string] => [b.id, b.name]));
  // The filter/table need one flat "brand name" space spanning both sources:
  // managed brands (ppf/tint/ceramic/other) and wrap materials' brand field.
  const brandOptions = Array.from(new Set([...managedBrands.map((b) => b.name), ...allMaterials.map((m) => m.brand)]))
    .sort()
    .map((name) => ({ value: name, label: name }));
  const materialLabel = (materialId: string | null) => {
    if (!materialId) return null;
    const m = allMaterials.find((mm) => mm.id === materialId);
    return m ? `${m.brand}${m.series ? ` ${m.series}` : ''}` : null;
  };
  // brandId covers ppf/tint/ceramic/other; wrap items (and any legacy item
  // still linked from before the brands table existed) fall back to their
  // linked material's brand/series.
  function brandLabel(item: InventoryItem): string | null {
    if (item.brandId) {
      const name = brandNameById.get(item.brandId);
      if (name) return name;
    }
    return materialLabel(item.materialId);
  }

  const hasActiveFilters = status !== undefined || category !== undefined || brand !== undefined || search !== '';
  function clearFilters() {
    setStatus(undefined);
    setCategory(undefined);
    setBrand(undefined);
    setSearch('');
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Two fixed rows rather than one flex-wrap row: filters always read
          top-to-bottom-left-to-right as their own group, and search/add stay
          pinned together on their own line instead of unpredictably reflowing
          into the filter row at narrower widths (Gestalt proximity: each row
          is one job, "narrow the list" vs. "find or create an item"). */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 pr-0.5 text-[11px] font-bold uppercase tracking-wide text-muted">
            <SlidersHorizontal size={12} /> {t('filters.label')}
          </span>
          <div className="flex border border-border p-0.5">
            {[undefined, 'low', 'out'].map((s) => (
              <button
                key={s ?? 'all'}
                onClick={() => setStatus(s)}
                className={cn('px-3 py-1.5 text-xs font-semibold capitalize', status === s ? 'bg-ink text-surface' : 'text-muted hover:text-ink')}
              >
                {s ? t(`filters.status.${s}`) : t('filters.all')}
              </button>
            ))}
          </div>
          <div className="flex border border-border p-0.5">
            <button
              onClick={() => setCategory(undefined)}
              className={cn('px-3 py-1.5 text-xs font-semibold', category === undefined ? 'bg-ink text-surface' : 'text-muted hover:text-ink')}
            >
              {t('filters.all')}
            </button>
            {INVENTORY_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={cn('px-3 py-1.5 text-xs font-semibold', category === c ? 'bg-ink text-surface' : 'text-muted hover:text-ink')}
              >
                {t(`inventoryCategory.${c}`)}
              </button>
            ))}
          </div>
          <div className="w-44">
            <Combobox value={brand ?? ''} onChange={(v) => setBrand(v || undefined)} options={brandOptions} placeholder={t('filters.allBrandsPlaceholder')} emptyText={t('empty.noBrands')} />
          </div>
          <button
            type="button"
            onClick={() => setManageBrandsOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-ink"
          >
            <Settings2 size={13} /> {t('actions.manageBrands')}
          </button>
          {hasActiveFilters ? (
            <button type="button" onClick={clearFilters} className="flex items-center gap-1 text-xs font-semibold text-muted hover:text-danger">
              <X size={12} /> {t('filters.clear')}
            </button>
          ) : null}
        </div>

        {/* Search-then-act row: search sits directly beside "Add item" since
            they're the two most frequent actions on this page (Fitts's Law -
            keep them close and both easy to reach), right-aligned where a
            left-to-right scan naturally ends. */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-60">
            <Search size={14} strokeWidth={1.8} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              placeholder={t('search.placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-8"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label={t('search.clearAriaLabel')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
              >
                <X size={13} />
              </button>
            ) : null}
          </div>
          <Button size="sm" className="ml-auto gap-1.5" onClick={() => setAddOpen(true)} data-tour="page-inventory-add">
            <Plus size={14} /> {t('actions.addItem')}
          </Button>
        </div>
      </div>

      {/* Mobile: tappable card list. Tables scroll poorly on phones, so the
          same rows render as a stacked card list below md. Tapping a card
          opens the same ItemDrawer as clicking the row on desktop - that
          Sheet is where adjust-stock/edit live, so no separate action
          controls are needed here. */}
      <div className="flex flex-col gap-2 md:hidden">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelected(item)}
            className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-3.5 text-left"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold text-ink">{item.name}</div>
                {item.sku ? <div className="truncate text-xs text-muted">{item.sku}</div> : null}
              </div>
              <span className={cn('shrink-0 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide', STATUS_STYLE[item.status])}>{t(`table.status.${item.status}`)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              <span>{t(`inventoryCategory.${item.category}`)}</span>
              <span className="truncate">{brandLabel(item) ?? t('table.noneBrand')}</span>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-1.5 text-xs">
              <span className="font-mono tabular-nums text-ink">{item.qtyOnHand} {item.unit}</span>
              <span className="font-mono tabular-nums text-muted">{t('table.headers.reorderPoint')} {item.reorderPoint} {item.unit}</span>
              <span className="font-mono tabular-nums text-muted">{formatMoney(item.priceCentsPerUnit)}</span>
            </div>
            {item.qtyReserved > 0 ? (
              <div className="font-mono text-[11px] tabular-nums text-muted">{t('table.reserved', { qty: item.qtyReserved, unit: item.unit })}</div>
            ) : null}
          </button>
        ))}
        {items.length === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
        ) : null}
      </div>

      <div className="hidden overflow-x-auto rounded-[var(--radius-card)] border border-border bg-surface md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2.5">{t('table.headers.item')}</th>
              <th className="px-4 py-2.5">{t('table.headers.category')}</th>
              <th className="px-4 py-2.5">{t('table.headers.brand')}</th>
              <th className="px-4 py-2.5">{t('table.headers.onHand')}</th>
              <th className="px-4 py-2.5">{t('table.headers.reorderPoint')}</th>
              <th className="px-4 py-2.5">{t('table.headers.pricePerUnit')}</th>
              <th className="px-4 py-2.5">{t('table.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-2" onClick={() => setSelected(item)}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-ink">{item.name}</div>
                  {item.sku ? <div className="text-xs text-muted">{item.sku}</div> : null}
                </td>
                <td className="px-4 py-3 text-muted">{t(`inventoryCategory.${item.category}`)}</td>
                <td className="px-4 py-3 text-muted">{brandLabel(item) ?? t('table.noneBrand')}</td>
                <td className="px-4 py-3 font-mono tabular-nums">
                  {item.qtyOnHand} {item.unit}
                  {item.qtyReserved > 0 ? (
                    <div className="text-[11px] font-normal text-muted">{t('table.reserved', { qty: item.qtyReserved, unit: item.unit })}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 font-mono tabular-nums text-muted">{item.reorderPoint} {item.unit}</td>
                <td className="px-4 py-3 font-mono tabular-nums text-muted">{formatMoney(item.priceCentsPerUnit)}</td>
                <td className="px-4 py-3">
                  <span className={cn(' px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide', STATUS_STYLE[item.status])}>{t(`table.status.${item.status}`)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
        ) : null}
      </div>

      {workspaceId ? <AddItemDialog open={addOpen} onOpenChange={setAddOpen} workspaceId={workspaceId} /> : null}
      {selected ? <ItemDrawer item={selected} workspaceId={workspaceId} onClose={() => setSelected(null)} /> : null}
      {workspaceId ? <ManageBrandsDialog open={manageBrandsOpen} onOpenChange={setManageBrandsOpen} workspaceId={workspaceId} /> : null}
    </div>
  );
}

function AddItemDialog({
  open, onOpenChange, workspaceId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string;
}) {
  const { t } = useTranslation('inventory');
  const createItem = useCreateInventoryItem(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InventoryItemFormValues>({ resolver: zodResolver(createInventoryItemSchema), defaultValues: { category: 'other', unit: 'sqft', initialQty: 0, reorderPoint: 0 } });

  const category = watch('category') as InventoryCategory;
  const isWrap = category === 'wrap';
  const materialId = watch('materialId') ?? '';
  const brandId = watch('brandId') ?? '';
  const fixedUnit = INVENTORY_CATEGORY_UNIT[category];
  const unitForPriceLabel = fixedUnit ?? (watch('unit') as string);
  // Cents-over-the-wire field entered in dollars - kept as its own local
  // state (not registered against the cents-typed schema field) so the
  // input can show/accept "12.50" rather than raw cent integers.
  const [priceDollars, setPriceDollars] = useState('');
  const [swatch, setSwatch] = useState<SwatchValue>(null);

  useEffect(() => {
    setValue('materialId', undefined);
    setValue('brandId', undefined);
  }, [category, setValue]);

  async function onSubmit(values: InventoryItemFormValues) {
    const unit = fixedUnit ?? values.unit;
    const materialId = isWrap ? (values.materialId || undefined) : undefined;
    const brandId = !isWrap ? (values.brandId || undefined) : undefined;
    const priceCentsPerUnit = priceDollars ? Math.round(Number(priceDollars) * 100) : undefined;
    await createItem.mutateAsync(createInventoryItemSchema.parse({ ...values, unit, materialId, brandId, priceCentsPerUnit, swatchMediaId: swatch?.id }));
    toast.success(t('toast.itemAdded'));
    reset();
    setPriceDollars('');
    setSwatch(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('addDialog.title')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">{t('form.labels.name')}</Label>
          <Input id="name" {...register('name')} />
          <FieldError>{errors.name?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="category">{t('form.labels.category')}</Label>
          <select id="category" {...register('category')} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
            {INVENTORY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`inventoryCategory.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          {isWrap ? (
            <BrandField workspaceId={workspaceId} category={category} value={materialId} onChange={(id) => setValue('materialId', id || undefined)} />
          ) : (
            <BrandComboField workspaceId={workspaceId} value={brandId} onChange={(id) => setValue('brandId', id || undefined)} />
          )}
          <p className="text-xs text-muted">{t('addDialog.brandHelp')}</p>
          {isWrap ? (
            <FieldError>{errors.materialId?.message}</FieldError>
          ) : (
            <FieldError>{errors.brandId?.message}</FieldError>
          )}
        </div>
        <SwatchUploadField workspaceId={workspaceId} value={swatch} onChange={setSwatch} />
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t('form.labels.unit')}</Label>
            {fixedUnit ? (
              <div className="flex h-10 items-center rounded-[var(--radius-input)] border border-border bg-surface-2 px-3 text-sm text-muted">{fixedUnit}</div>
            ) : (
              <select {...register('unit')} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm">
                {INVENTORY_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="initialQty">{t('form.labels.onHand')}</Label>
            <Input id="initialQty" type="number" step="0.01" {...register('initialQty', { valueAsNumber: true })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reorderPoint">{t('form.labels.reorderAt')}</Label>
            <Input id="reorderPoint" type="number" step="0.01" {...register('reorderPoint', { valueAsNumber: true })} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="priceCentsPerUnit">{t(pricePerUnitLabelKey(unitForPriceLabel))}</Label>
          <Input id="priceCentsPerUnit" type="number" step="0.01" min="0" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} />
        </div>
        <Button type="submit" loading={createItem.isPending} className="mt-1 w-full">
          {t('actions.addItem')}
        </Button>
      </form>
    </Dialog>
  );
}

// Purchase/shrinkage always imply a direction; only adjustment (a plain
// recount correction) can go either way, so each direction offers it as the
// second option rather than showing all three reasons for both directions.
const REASONS_BY_DIRECTION = {
  add: ['purchase', 'adjustment'],
  remove: ['shrinkage', 'adjustment'],
} as const;

function ItemDrawer({
  item, workspaceId, onClose,
}: {
  item: InventoryItem;
  workspaceId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { formatDateTime, formatMoney } = useLocaleFormatters();
  const { data: movements } = useInventoryMovements(workspaceId, item.id);
  const allMaterials = useAllMaterials(workspaceId);
  const { data: brandsData } = useBrands(workspaceId);
  const adjustInventory = useAdjustInventory(workspaceId);
  const updateItem = useUpdateInventoryItem(workspaceId);
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<AdjustInventoryInput['reason']>('purchase');
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState(item.name);
  const [category, setCategory] = useState<InventoryCategory>(item.category);
  const [materialId, setMaterialId] = useState(item.materialId ?? '');
  const [brandId, setBrandId] = useState(item.brandId ?? '');
  const [priceDollars, setPriceDollars] = useState(centsToDollarsInput(item.priceCentsPerUnit));
  const [swatch, setSwatch] = useState<SwatchValue>(item.swatchMediaId ? { id: item.swatchMediaId, thumbUrl: item.swatchThumbUrl } : null);
  const isWrap = category === 'wrap';

  const linkedMaterial = item.materialId ? allMaterials.find((m) => m.id === item.materialId) : undefined;
  const managedBrandName = item.brandId ? brandsData?.items.find((b) => b.id === item.brandId)?.name : undefined;
  const displayBrand = managedBrandName ?? (linkedMaterial ? `${linkedMaterial.brand}${linkedMaterial.series ? ` ${linkedMaterial.series}` : ''}` : null);

  async function handleSaveDetails() {
    const priceCentsPerUnit = priceDollars ? Math.round(Number(priceDollars) * 100) : undefined;
    const swatchMediaId = swatch?.id ?? null;
    const swatchChanged = swatchMediaId !== item.swatchMediaId;
    await updateItem.mutateAsync({
      itemId: item.id,
      input: {
        name: name.trim(),
        category,
        materialId: isWrap ? (materialId || null) : null,
        brandId: !isWrap ? (brandId || null) : null,
        priceCentsPerUnit,
        ...(swatchChanged ? { swatchMediaId } : {}),
      },
    });
    toast.success(t('toast.itemUpdated'));
    setEditOpen(false);
  }

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()} title={item.name}>
      <div className="flex flex-col gap-5">
        <div>
          <div className="font-mono text-3xl font-bold tabular-nums">{item.qtyOnHand} <span className="text-base font-normal text-muted">{item.unit}</span></div>
          {item.qtyReserved > 0 ? (
            <div className="text-xs text-muted">
              {t('drawer.reserved', { qty: item.qtyReserved, unit: item.unit })}
              {' · '}
              {t('drawer.available', { qty: item.qtyOnHand - item.qtyReserved, unit: item.unit })}
            </div>
          ) : null}
          <div className="text-xs text-muted">{t('drawer.reorderAt', { point: item.reorderPoint, unit: item.unit })}</div>
          <div className="text-xs text-muted">{t('drawer.pricePerUnit', { price: formatMoney(item.priceCentsPerUnit) })}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="w-fit bg-surface-2 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-ink">{t(`inventoryCategory.${item.category}`)}</span>
            {displayBrand ? <span className="text-xs text-muted">{t('drawer.brandLabel', { brand: displayBrand })}</span> : null}
            <button type="button" onClick={() => setEditOpen((v) => !v)} className="ml-auto text-xs font-semibold text-accent-ink hover:underline">
              {editOpen ? t('common:actions.cancel') : t('common:actions.edit')}
            </button>
          </div>
          {editOpen ? (
            <div className="mt-2 flex flex-col gap-2 rounded-[var(--radius-input)] border border-border p-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="itemName">{t('form.labels.name')}</Label>
                <Input id="itemName" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.labels.category')}</Label>
                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as InventoryCategory);
                    setMaterialId('');
                    setBrandId('');
                  }}
                  className="h-9 rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm"
                >
                  {INVENTORY_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`inventoryCategory.${c}`)}
                    </option>
                  ))}
                </select>
              </div>
              {isWrap ? (
                <BrandField workspaceId={workspaceId} category={category} value={materialId} onChange={setMaterialId} />
              ) : (
                <BrandComboField workspaceId={workspaceId} value={brandId} onChange={setBrandId} />
              )}
              <SwatchUploadField workspaceId={workspaceId} value={swatch} onChange={setSwatch} />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="itemPricePerUnit">{t(pricePerUnitLabelKey(item.unit))}</Label>
                <Input id="itemPricePerUnit" type="number" step="0.01" min="0" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} />
              </div>
              <Button size="sm" disabled={!name.trim()} loading={updateItem.isPending} onClick={handleSaveDetails}>
                {t('common:actions.save')}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 rounded-[var(--radius-input)] border border-border p-3">
          <div className="text-xs font-bold uppercase tracking-wide text-muted">{t('drawer.adjustStock')}</div>
          <div className="flex border border-border p-0.5">
            {(['add', 'remove'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDirection(d);
                  setReason(REASONS_BY_DIRECTION[d][0]);
                }}
                className={cn('flex-1 px-3 py-1.5 text-xs font-semibold capitalize', direction === d ? 'bg-ink text-surface' : 'text-muted')}
              >
                {t(`drawer.direction.${d}`)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input type="number" step="0.01" min="0" placeholder={t('form.placeholders.amount')} value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1" />
            <select value={reason} onChange={(e) => setReason(e.target.value as typeof reason)} className="rounded-[var(--radius-input)] border border-border bg-surface px-2 text-sm">
              {REASONS_BY_DIRECTION[direction].map((r) => (
                <option key={r} value={r}>{t(`reasons.${r}`)}</option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            disabled={!amount || Number(amount) <= 0}
            loading={adjustInventory.isPending}
            onClick={async () => {
              const delta = direction === 'add' ? Number(amount) : -Number(amount);
              await adjustInventory.mutateAsync({ itemId: item.id, input: { delta, reason } });
              setAmount('');
              toast.success(t('toast.stockUpdated'));
            }}
          >
            {t('actions.apply')}
          </Button>
        </div>

        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">{t('drawer.history')}</div>
          <div className="flex flex-col gap-2.5">
            {(movements?.items ?? []).map((m) => (
              <div key={m.id} className="flex justify-between border-l-2 border-border pl-3 text-sm">
                <div>
                  <span className="font-semibold capitalize">{t(`reasons.${m.reason}`, { defaultValue: m.reason.replace('_', ' ') })}</span>
                  {m.note ? <span className="text-muted"> - {m.note}</span> : null}
                  <div className="text-xs text-muted">{formatDateTime(new Date(m.createdAt))}</div>
                </div>
                <div className={cn('font-mono font-semibold', m.delta >= 0 ? 'text-success' : 'text-danger')}>
                  {m.delta >= 0 ? '+' : ''}{m.delta}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  );
}

// Add/rename/remove for the standalone brands table (0032_brands.sql), used
// to tag PPF/Tint/Ceramic/Other items. Wrap's brand tiers (cost, price
// multiplier) are a bigger concept than a name, so they're still managed in
// Settings > Pricing > Wrap, not here.
function ManageBrandsDialog({ open, onOpenChange, workspaceId }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string }) {
  const { t } = useTranslation('inventory');
  const { data } = useBrands(workspaceId);
  const createBrand = useCreateBrand(workspaceId);
  const updateBrand = useUpdateBrand(workspaceId);
  const deleteBrand = useDeleteBrand(workspaceId);
  const [newName, setNewName] = useState('');
  const { confirm, ConfirmDialog } = useConfirm();

  async function handleCreate() {
    if (!newName.trim()) return;
    await createBrand.mutateAsync({ name: newName.trim() });
    setNewName('');
    toast.success(t('toast.brandAdded'));
  }

  async function handleDelete(id: string, name: string) {
    const ok = await confirm({
      title: t('manageBrandsDialog.removeConfirm.title', { name }),
      description: t('manageBrandsDialog.removeConfirm.description'),
      confirmLabel: t('actions.remove'),
      variant: 'danger',
    });
    if (!ok) return;
    await deleteBrand.mutateAsync(id);
    toast.success(t('toast.brandRemoved'));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('actions.manageBrands')} description={t('manageBrandsDialog.description')}>
      <ConfirmDialog />
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Input
            placeholder={t('form.placeholders.newBrandName')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="flex-1"
          />
          <Button size="sm" disabled={!newName.trim()} loading={createBrand.isPending} onClick={handleCreate}>
            <Plus size={14} /> {t('actions.add')}
          </Button>
        </div>
        <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {(data?.items ?? []).map((b) => (
            <div key={b.id} className="flex items-center gap-2 border-b border-border py-1.5 last:border-0">
              <Input
                defaultValue={b.name}
                key={`${b.id}-${b.name}`}
                className="h-8 flex-1"
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next && next !== b.name) updateBrand.mutate({ brandId: b.id, input: { name: next } });
                }}
              />
              <button type="button" onClick={() => handleDelete(b.id, b.name)} className="p-1 text-muted hover:text-danger">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {(data?.items.length ?? 0) === 0 ? <p className="py-2 text-sm text-muted">{t('empty.noBrands')}</p> : null}
        </div>
      </div>
    </Dialog>
  );
}
